import type { SceneGroup } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';

import { ApiService } from 'js/src/client';
import { getBlueprint } from 'js/src/data/blueprints/blueprints-selectors';
import { getCaptionsCookie } from 'js/src/data/media/cookie-operations';
import { getCaptionsSceneDesignsForTemplate } from 'js/src/data/templates/templates-selectors';
import { setObjectPropertyValue } from 'js/src/editor/util';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { getAccentColorsList } from 'js/src/luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

interface BlueprintCaptionSettings {
    visible: boolean;
    sceneDesignId?: string;
    fontUrl?: string;
    fillColor?: number;
    textSize?: number;
    textAlign?: string;
    leadingMultiplier?: number;
    spokenWordFontUrl?: string;
    spokenWordFillColor?: number;
    isSpokenWordStyleEnabled?: boolean;
    accentColors?: Array<{
        path: string;
        color: number | string;
    }>;
}

type CaptionAssetLike = {
    normalStyle?: { fontUrl?: string; fill?: number };
    spokenWordStyle?: { fontUrl?: string; fill?: number };
    textSize?: number;
    textAlign?: string;
    leadingMultiplier?: number;
    isSpokenWordStyleEnabled?: boolean;
};

type CaptionStyleSettings = Omit<BlueprintCaptionSettings, 'visible' | 'accentColors'>;

/**
 * Applies blueprint caption styles to a caption asset. Uses explicit
 * `!== undefined` checks so falsy-but-meaningful values (e.g. fillColor 0 for
 * black) are still applied.
 */
export const applyBlueprintCaptionStylesToAsset = (
    captionAsset: CaptionAssetLike,
    settings: CaptionStyleSettings,
) => {
    if (settings.fontUrl !== undefined) {
        setObjectPropertyValue(captionAsset, 'normalStyle.fontUrl', settings.fontUrl);
    }
    if (settings.fillColor !== undefined) {
        setObjectPropertyValue(captionAsset, 'normalStyle.fill', settings.fillColor);
    }
    if (settings.spokenWordFontUrl !== undefined) {
        setObjectPropertyValue(captionAsset, 'spokenWordStyle.fontUrl', settings.spokenWordFontUrl);
    }
    if (settings.spokenWordFillColor !== undefined) {
        setObjectPropertyValue(captionAsset, 'spokenWordStyle.fill', settings.spokenWordFillColor);
    }
    if (settings.isSpokenWordStyleEnabled !== undefined) {
        captionAsset.isSpokenWordStyleEnabled = settings.isSpokenWordStyleEnabled;
    }
    if (settings.textSize !== undefined) {
        captionAsset.textSize = settings.textSize;
    }
    if (settings.textAlign !== undefined) {
        captionAsset.textAlign = settings.textAlign;
    }
    if (settings.leadingMultiplier !== undefined) {
        captionAsset.leadingMultiplier = settings.leadingMultiplier;
    }
};

/**
 * Applies accent (asset) colors from blueprint settings to the destination
 * caption asset.
 *
 * Each accent color carries the source video's `assetId`, but that UUID does
 * not exist on the destination caption display group. The color path itself
 * (e.g. `normalStyle.graphics.0.fill`) is a path on the caption asset, so we
 * apply it directly to `captionAsset`, matching how `setCaptionsColorOperation`
 * applies user-driven color changes.
 */
export const applyBlueprintAccentColorsToCaptionAsset = (
    captionAsset: object,
    accentColors: ReadonlyArray<{ path: string; color: number | string }>,
) => {
    accentColors.forEach((accentColor) => {
        setObjectPropertyValue(captionAsset, accentColor.path, accentColor.color);
    });
};

/**
 * Picks the scene group whose captions are the source of truth for the
 * blueprint's caption style.
 *
 * Most example videos have several scene groups with a `captions` field, but
 * only voicedriven scene groups carry the user-configured caption style — they
 * have a non-null `captions.sceneDesignId`. Placeholder scene groups have
 * `sceneDesignId: null` and default asset values, so picking the first one
 * (which is what `Array.find(sg => sg.captions)` would do) yields defaults
 * instead of the blueprint's styling.
 */
export const pickConfiguredCaptionSceneGroup = <
    SG extends { captions?: { sceneDesignId?: string | null } | null },
>(
    sceneGroups: readonly SG[],
): SG | undefined => {
    const configured = sceneGroups.find((sg) => sg.captions && sg.captions.sceneDesignId);
    if (configured) return configured;
    return sceneGroups.find((sg) => sg.captions);
};

const fetchBlueprintCaptionSettings = async (
    blueprintId: number,
    templateId: string,
): Promise<BlueprintCaptionSettings | null> => {
    try {
        // Fetch template style video data from blueprint
        const response = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
            path: { id: blueprintId },
            query: { template: templateId },
        });

        // Extract caption settings
        const dataLuminary = new Luminary({
            fetchResources: false,
            disableRenderer: true,
            autoDraw: false,
            autoUpdate: false,
        });
        dataLuminary.load(JSON.parse(response.data.video_data));

        const sceneGroupWithCaptions = pickConfiguredCaptionSceneGroup(dataLuminary.sceneGroups);
        if (sceneGroupWithCaptions) {
            const captionAsset = sceneGroupWithCaptions.captions.asset;

            let accentColors: Array<{ path: string; color: number | string }> = [];
            if (captionAsset) {
                accentColors = getAccentColorsList(captionAsset).map((accentColor) => ({
                    path: accentColor.path,
                    color: accentColor.color,
                }));
            }

            // Cast to read FancyCaptionAsset-only fields (spokenWordStyle, isSpokenWordStyleEnabled).
            // These are undefined on plain CaptionAsset and are filtered out by the
            // `!== undefined` checks in applyBlueprintCaptionStylesToAsset.
            const fancyAsset = captionAsset as
                | (typeof captionAsset & {
                      spokenWordStyle?: { fontUrl?: string; fill?: number };
                      isSpokenWordStyleEnabled?: boolean;
                      textAlign?: string;
                      leadingMultiplier?: number;
                  })
                | undefined;

            return {
                visible: sceneGroupWithCaptions.captions.visible,
                sceneDesignId: sceneGroupWithCaptions.captions.sceneDesignId ?? undefined,
                fontUrl: captionAsset?.normalStyle?.fontUrl,
                fillColor: captionAsset?.normalStyle?.fill,
                textSize: captionAsset?.textSize,
                textAlign: fancyAsset?.textAlign,
                leadingMultiplier: fancyAsset?.leadingMultiplier,
                spokenWordFontUrl: fancyAsset?.spokenWordStyle?.fontUrl,
                spokenWordFillColor: fancyAsset?.spokenWordStyle?.fill,
                isSpokenWordStyleEnabled: fancyAsset?.isSpokenWordStyleEnabled,
                accentColors,
            };
        }

        return null;
    } catch (error) {
        ErrorReporter.capture(error);
        return null;
    }
};

/**
 * Sets caption enabled (and style) on a scene group from blueprint settings or cookies
 * If blueprint is available, it takes priority over cookies.
 * Cookies is only used to turn captions on or off (not styling).
 */
export const setCaptionsFromBlueprintOrCookiesOperation = createLuminaryOperation(
    async (
        { getState },
        sceneGroup: SceneGroup,
        blueprintId: number | null,
        templateId: string,
        defaultSceneDesignId: string | null,
    ): Promise<void> => {
        // Set default scene design first, if not already set
        const captionsSceneDesignIdOptions = getCaptionsSceneDesignsForTemplate(
            getState(),
            templateId,
        ).map((sceneDesign) => sceneDesign.id);
        const existingSceneDesignId = sceneGroup.captions.sceneDesignId;

        if (
            !existingSceneDesignId ||
            !captionsSceneDesignIdOptions.includes(existingSceneDesignId)
        ) {
            sceneGroup.captions.sceneDesignId = defaultSceneDesignId;
        }

        // Try blueprint first
        if (blueprintId && templateId) {
            try {
                // First, check if blueprint has caption_scene_design_id set directly
                const blueprint = getBlueprint(getState(), blueprintId);
                if (blueprint?.caption_scene_design_id) {
                    sceneGroup.captions.sceneDesignId = blueprint.caption_scene_design_id;
                }

                // Fetch additional styling settings from blueprint video data
                const blueprintSettings = await fetchBlueprintCaptionSettings(
                    blueprintId,
                    templateId,
                );
                if (blueprintSettings) {
                    // Use sceneDesignId from settings only if not already set from blueprint field
                    if (blueprintSettings.sceneDesignId && !blueprint?.caption_scene_design_id) {
                        sceneGroup.captions.sceneDesignId = blueprintSettings.sceneDesignId;
                    }

                    const captionAsset = sceneGroup.captions.asset;
                    if (captionAsset) {
                        applyBlueprintCaptionStylesToAsset(captionAsset, blueprintSettings);
                    }

                    if (captionAsset && blueprintSettings.accentColors?.length) {
                        applyBlueprintAccentColorsToCaptionAsset(
                            captionAsset,
                            blueprintSettings.accentColors,
                        );
                    }

                    sceneGroup.captions.visibleWithUpdate = blueprintSettings.visible;

                    return;
                }
            } catch (error) {
                // Error fetching blueprint styling settings, keep default
            }
        }

        // fallback to cookies
        sceneGroup.captions.visibleWithUpdate = getCaptionsCookie() ?? false;
    },
);
